
import api from "./api";

/* ---------------- GET LEAVES ---------------- */

export const getLeaves = async () => {
  const res = await api.get(`/salary/leave_applications`);

  const raw = res.data?.data || [];

  const leaves = raw.map((l: any) => ({
    _id: String(l.id),
    employeeId: l.employeeID,
    employeeName: l.name || "Employee",
    leaveType: l.leaveType || "personal",
    startDate: l.fromDate,
    endDate: l.toDate,
    days: l.totalDays || 0,
    reason: l.reason,
    status: l.status?.toLowerCase(),
    appliedDate: l.applied_on || l.decision_on,
    approvedBy: l.decision_by || null,
    managerID: l.managerID || null,
    managerComments: "",
    workedOn: l.worked_on || null,
    session: l.session || null,
    // Days this leave ran past the balance, what the approver decided about
    // them, and — for a carry-forward — the obligation that came with it. Null
    // for everything entitlement covered, which is almost all of them.
    unpaidDays: typeof l.unpaidDays === "number" ? l.unpaidDays : null,
    overdraftDisposition: (l.overdraftDisposition || null) as
      | "no_deduction"
      | "carry_forward"
      | null,
    makeupTo: l.makeupTo || null,
    makeupHours: typeof l.makeupHours === "number" ? l.makeupHours : null,
    recoveryChoice: (l.recoveryChoice || null) as "hours" | "money" | null,
    makeupAmount: typeof l.makeupAmount === "number" ? l.makeupAmount : null,
    obligationState: (l.obligationState || null) as
      | "none"
      | "open"
      | "fulfilled"
      | "shortfall"
      | "cancelled"
      | null,
    // Overtime taken as leave: present only on an overtime_earn request.
    conversion: (l.conversion || null) as LeaveConversion | null,
  }));

  return { leaves };
};

// -------------SHOW LEAVE EXCLUDE EMPLOYEE---------
export const getMyLeaves = async () => {
  const res = await api.get(`/salary/leave_applications_all`);

  const raw = res.data?.data || [];

  const Leaves = raw.map((l: any) => ({
    _id: String(l.id),
    employeeId: l.employeeID,
    employeeName: l.name || "Employee",
    leaveType: l.leaveType || "personal",
    startDate: l.fromDate,
    endDate: l.toDate,
    days: l.totalDays || 0,
    reason: l.reason,
    status: l.status?.toLowerCase(),
    appliedDate: l.applied_on || l.decision_on,
    approvedBy: l.decision_by || null,
    managerID: l.managerID || null,
    managerComments: "",
    workedOn: l.worked_on || null,
    session: l.session || null,
    // Days this leave ran past the balance, what the approver decided about
    // them, and — for a carry-forward — the obligation that came with it. Null
    // for everything entitlement covered, which is almost all of them.
    unpaidDays: typeof l.unpaidDays === "number" ? l.unpaidDays : null,
    overdraftDisposition: (l.overdraftDisposition || null) as
      | "no_deduction"
      | "carry_forward"
      | null,
    makeupTo: l.makeupTo || null,
    makeupHours: typeof l.makeupHours === "number" ? l.makeupHours : null,
    recoveryChoice: (l.recoveryChoice || null) as "hours" | "money" | null,
    makeupAmount: typeof l.makeupAmount === "number" ? l.makeupAmount : null,
    obligationState: (l.obligationState || null) as
      | "none"
      | "open"
      | "fulfilled"
      | "shortfall"
      | "cancelled"
      | null,
    // Overtime taken as leave: present only on an overtime_earn request.
    conversion: (l.conversion || null) as LeaveConversion | null,
  }));

  return { Leaves };
};


/* ---------------- APPLY LEAVE ---------------- */
export const applyLeave = async (payload: {
  employeeID: string;
  managerID: string;
  name: string;
  leaveType: string;
  fromDate: string;
  toDate: string;
  totalDays: number;
  reason: string;
  status: string;
  // Set only when the leaveType is the compensatory earn marker.
  worked_on?: string;
  // Half-day leave (policy rule `leave.half_day`). Absent for a whole day.
  // The server re-derives totalDays from this rather than trusting the
  // client's 0.5, so the two cannot disagree.
  session?: "first_half" | "second_half" | string;
}) => {
  const res = await api.post("/salary/apply_leave", payload);
  return res.data;
};

/* ---------------- APPLY FOR COMPENSATORY LEAVE (EARN) ---------------- */
export const applyCompensatoryEarn = async (payload: {
  employeeID: string | null;
  managerID: string | null;
  epmtID: string | null;
  name: string;
  fromDate: string; // YYYY-MM-DD
  toDate: string;   // YYYY-MM-DD
  days: number;
  reason?: string;
  workedOn?: string; // YYYY-MM-DD — optional context for backwards compat
}) => {
  const res = await api.post("/salary/apply_leave", {
    employeeID: payload.employeeID,
    epmtID: payload.epmtID,
    managerID: payload.managerID || "",
    name: payload.name,
    leaveType: "compensatory_earn",
    fromDate: payload.fromDate,
    toDate: payload.toDate,
    totalDays: payload.days,
    reason: payload.reason || "",
    status: "PENDING",
    appliedOn: new Date().toISOString(),
    worked_on: payload.workedOn || undefined,
  });
  return res.data;
};


/* ---------------- APPLY FOR MATERNITY / PATERNITY LEAVE (EARN) ----- */

async function applyParentalEarn(
  marker: "maternity_earn" | "paternity_earn",
  payload: {
    employeeID: string | null;
    managerID: string | null;
    epmtID: string | null;
    name: string;
    fromDate: string; // YYYY-MM-DD
    toDate: string;   // YYYY-MM-DD
    days: number;
    reason?: string;
  },
) {
  const res = await api.post("/salary/apply_leave", {
    employeeID: payload.employeeID,
    epmtID: payload.epmtID,
    managerID: payload.managerID || "",
    name: payload.name,
    leaveType: marker,
    fromDate: payload.fromDate,
    toDate: payload.toDate,
    totalDays: payload.days,
    reason: payload.reason || "",
    status: "PENDING",
    appliedOn: new Date().toISOString(),
  });
  return res.data;
}

export const applyMaternityEarn = (payload: {
  employeeID: string | null;
  managerID: string | null;
  epmtID: string | null;
  name: string;
  fromDate: string;
  toDate: string;
  days: number;
  reason?: string;
}) => applyParentalEarn("maternity_earn", payload);

export const applyPaternityEarn = (payload: {
  employeeID: string | null;
  managerID: string | null;
  epmtID: string | null;
  name: string;
  fromDate: string;
  toDate: string;
  days: number;
  reason?: string;
}) => applyParentalEarn("paternity_earn", payload);


/* ---------------- OVERTIME TAKEN AS LEAVE ---------------- */

export interface LeaveConversion {
  state: "requested" | "approved" | "settled" | "cancelled";
  rate: number;
  hours_requested: number | null;
  /** 0 until the period closes and the conversion settles. */
  hours_converted: number;
  credited_days: number;
  settled_on: string | null;
}

/* ---------------- APPROVE / REJECT ---------------- */
/**
 * What happens to days entitlement could not pay for.
 * - no_deduction: written off; nothing is recovered.
 * - carry_forward: worked back as hours inside a period, and whatever is left
 *   at the end of it is deducted from that month's salary.
 */
export type OverdraftDisposition = "no_deduction" | "carry_forward";

/** The period a carry-forward has to be worked back in: a whole month, or exact dates. */
export interface MakeupPeriod {
  makeup_month?: string | null;   // "YYYY-MM"
  makeup_from?: string | null;    // "YYYY-MM-DD"
  makeup_to?: string | null;
}

export const updateLeaveStatus = async (
  leaveId: string,
  status: "approved" | "rejected",
  comments?: string,
  // Required by the server when the request runs past the employee's balance.
  // Omitting it on such a leave comes back as a 400 with code
  // OVERDRAFT_DISPOSITION_REQUIRED and the numbers to show; a carry_forward
  // with no period comes back as MAKEUP_PERIOD_INVALID.
  disposition?: OverdraftDisposition | null,
  period?: MakeupPeriod | null
) => {
  const backendStatus = status.toUpperCase();

  const res = await api.put(
    `/salary/approve_leave/${leaveId}?status=${backendStatus}`,
    {
      disposition: disposition || null,
      comments: comments || null,
      ...(period || {}),
    }
  );

  return res.data;
};

/* ---------------- DAYS BEYOND ENTITLEMENT ---------------- */

export interface MakeupObligation {
  state: "none" | "open" | "fulfilled" | "shortfall" | "cancelled";
  from: string | null;
  to: string | null;
  /**
   * The unit the employee chose to settle in. Null until they answer, which
   * runs the hours. On "money" the hours figures still move, but they are not
   * the debt — the amounts below are.
   */
  recovery_choice: "hours" | "money" | null;
  hours_required: number;
  hours_done: number;
  hours_left: number;
  /** Null unless they chose money: the amount is only priced then. */
  amount_required: number | null;
  /** What the period's overtime has earned so far, in rupees. */
  amount_done: number;
  amount_left: number | null;
  days_credited: number;
  /** Null while the period is still running. */
  shortfall_days: number | null;
  days_remaining_in_period: number;
  settled_on: string | null;
  penalty_id: string | null;
}

export interface OverdraftPreview {
  leave_id: string;
  overdraws: boolean;
  leave_type?: string;
  charged_days?: number;
  available?: number;
  covered_days?: number;
  unpaid_days?: number;
  unpaid_dates?: string[];
  /** Set once decided; null while the leave is still pending. */
  disposition?: OverdraftDisposition | null;
  /** Hours a carry-forward would cost, and whether it is even possible. */
  shift_hours?: number;
  makeup_hours_required?: number;
  /** False when overtime is unavailable to them — the option cannot be offered. */
  makeup_available?: boolean;
  /** First month whose payroll is still open, as "YYYY-MM". */
  suggested_month?: string;
  /**
   * The leave's own month can be used when the leave fell in it: the period
   * then starts the day after the leave ended, which this gives as
   * "YYYY-MM-DD". Null when the leave belongs to another month, in which case
   * no period may start before today.
   */
  same_month_from?: string | null;
  /** Present once a carry-forward has been decided. */
  obligation?: MakeupObligation | null;
}

export const getOverdraftPreview = async (leaveId: string) => {
  const res = await api.get(`/salary/leave/${leaveId}/overdraft_preview`);
  return res.data as OverdraftPreview;
};

export const updateOverdraftDisposition = async (
  leaveId: string,
  disposition: OverdraftDisposition,
  period?: MakeupPeriod | null
) => {
  const res = await api.put(`/salary/leave/${leaveId}/overdraft`, {
    disposition,
    ...(period || {}),
  });
  return res.data;
};

/* ---------------- LEAVE BALANCE (EMPLOYEE ONLY) ---------------- */
// export const getLeaveBalance = async () => {
//   try {
//     const res = await api.get(
//       `/salary/get_details_for_leave_application`
//     );

//     return {
//       balance: res.data?.data || {},
//     };
//   } catch (err: any) {
//     return { balance: {} };
//   }
// };

export type HalfDayRange = { from: string | null; to: string | null } | null;
export type HalfDayRanges = Partial<Record<"first_half" | "second_half", HalfDayRange>>;

export const getLeaveBalance = async () => {
  const res = await api.get(
    `/salary/get_details_for_leave_application`
  );

  return {
    balance: res.data?.data?.balance || {},
    managerID: res.data?.data?.managerID || null,
    employeeID: res.data?.data?.employeeID || null,
    epmtID: res.data?.data?.id || null,
    gender: (res.data?.data?.gender || "").toString().toLowerCase() || null,
    // "YYYY-MM" for every month whose payroll is already approved. A
    // compensatory claim reaching into one of these is refused server-side,
    // so the form says so up front instead of on submit.
    payrollLockedMonths: (res.data?.data?.payroll_locked_months || []) as string[],
    // Which half-day sessions this person may ask for. The server resolves it
    // (the rule is per-branch, date-windowed and can be predicated on the
    // person), so the form only renders what comes back. An empty list means
    // the control is not offered at all.
    halfDay: {
      enabled: Boolean(res.data?.data?.half_day?.enabled),
      sessions: (res.data?.data?.half_day?.sessions || []) as string[],
      // The halves as clock ranges (attendance.half_day in clock-time mode),
      // so the form can say which hours are off and when the person is
      // expected. Absent in hours/percent mode.
      ranges: (res.data?.data?.half_day?.ranges || {}) as HalfDayRanges,
    },
  };
};


/* ---------------- CANCEL (OPTIONAL) ---------------- */
export const cancelLeave = async (leaveId: string) => {
  const res = await api.put(`/salary/cancel_leave/${leaveId}`);
  return res.data;
};

/* ---------------- NOTIFICATION BELL FEED ---------------- */
// Leave approve/reject/apply notifications ride the shared notification bell
// via a namespaced task_id "leave:<id>" (they have no real Task). The bell
// hydrates the unread feed from here and marks rows read on dismissal.
export interface LeaveNotification {
  id: string;
  kind: string; // APPLIED | APPROVED | REJECTED
  source: "leave";
  leave_id: string | null;
  task_id: string; // "leave:<id>"
  title: string;
  body: string | null;
  is_read: boolean;
  created_at: string;
}

export const getLeaveNotifications = (limit = 50) =>
  api.get<{ total: number; items: LeaveNotification[] }>(
    `/salary/leave_notifications/unread?limit=${limit}`
  );

export const markLeaveNotificationRead = (id: string) =>
  api.post(`/salary/leave_notifications/${id}/read`);
